import React, { useMemo } from 'react';
import { useTranslation } from '@shared/core';
import { ButtonV2Props } from '@withjoy/joykit';
import { AccommodationListingDTO, SortOption } from '@apps/accommodations/AccommodationsConsole.types';
import { createContext } from '@shared/utils/createContext';

/**
 * Map layout mode for the accommodations console.
 *
 * - `full`: Map is always visible and takes up a portion of the screen alongside listings.
 *   Use this for desktop views where you want the map and listings side-by-side.
 *
 * - `compact`: Map is hidden by default and can be expanded by the user.
 *   Use this for space-constrained layouts or when you want to prioritize listings visibility.
 */
export enum MapLayoutMode {
  COMPACT = 'compact',
  FULL = 'full',
}

export type AccommodationsConsoleConfig = Readonly<{
  topBar: {
    searchForm: {
      dateRangeEnabled: boolean;
      occupancySelectorEnabled: boolean;
    };
    backButton?: {
      label?: string;
    };
    showCloseButton?: boolean;
    showJoyLogo?: boolean;
  };
  layout: {
    mapLayout: {
      mode: MapLayoutMode;
    };
    skipCouplePicks?: boolean;
  };
  map: {
    shouldShowVenueMapPin?: boolean;
  };
  filtersPanel: {
    hideEventNameAndNote: boolean;
    shouldShowJoyTravelBenefitsFilter: boolean;
  };
  listingsPanel: {
    listingData: {
      shouldShowAllAccommodations: boolean;
      shouldFilterOutNonJoyRoomBlocks: boolean;
    };
    sortConfig: {
      sortOptions: [SortOption, string][];
      defaultSortOption: SortOption;
    };
    listingEntry: {
      roomBlockTag: string;
      shouldShowCouplePicksTag: boolean;
      shouldShowAddedTag: boolean;
      shouldShowRoomBlockNoteAndDate: boolean;
      bookingCta: {
        default: {
          buttonTitle: string;
          buttonProps: ButtonV2Props;
        };
        couplePicks?: {
          buttonTitle: string;
          buttonProps: ButtonV2Props;
        };
        onButtonClick?: (hotel: AccommodationListingDTO) => void;
        telemetryName?: string;
        telemetryNameForCouplePicks?: string;
      };
    };
  };
}>;

type AccommodationsConsoleConfigProviderProps = Readonly<{
  config?: AccommodationsConsoleConfig;
  children: React.ReactNode;
}>;

const [Provider, useAccommodationsConsoleConfig] = createContext<AccommodationsConsoleConfig>({
  name: 'AccommodationsConsoleConfig',
  strict: true,
  errorMessage: 'useAccommodationsConsoleConfig must be used within AccommodationsConsoleConfigProvider',
});

const AccommodationsConsoleConfigProvider = ({ config, children }: AccommodationsConsoleConfigProviderProps) => {
  const { t } = useTranslation('accommodationsConsole');
  const listingPanelTrans = t('listingsPanel');
  const sortTrans = t('sort');
  const contextValue = useMemo(() => {
    const defaultConfig: AccommodationsConsoleConfig = {
      topBar: {
        searchForm: {
          dateRangeEnabled: true,
          occupancySelectorEnabled: true,
        },
        showCloseButton: true,
        showJoyLogo: false,
      },
      layout: {
        mapLayout: {
          mode: MapLayoutMode.FULL,
        },
        skipCouplePicks: false,
      },
      map: {
        shouldShowVenueMapPin: true,
      },
      filtersPanel: {
        hideEventNameAndNote: false,
        shouldShowJoyTravelBenefitsFilter: false,
      },
      listingsPanel: {
        listingData: {
          shouldShowAllAccommodations: true,
          shouldFilterOutNonJoyRoomBlocks: false,
        },
        sortConfig: {
          sortOptions: [
            [SortOption.COUPLES_PICKS, sortTrans.couplesPicks()],
            [SortOption.TOP_MATCHES, sortTrans.topMatches()],
            [SortOption.PRICE_LOW_TO_HIGH, sortTrans.priceLowToHigh()],
            [SortOption.PRICE_HIGH_TO_LOW, sortTrans.priceHighToLow()],
            [SortOption.MOST_SAVINGS, sortTrans.mostSavings()],
            [SortOption.DISTANCE, sortTrans.distance()],
          ],
          defaultSortOption: SortOption.COUPLES_PICKS,
        },
        listingEntry: {
          roomBlockTag: '',
          shouldShowCouplePicksTag: true,
          shouldShowAddedTag: false,
          shouldShowRoomBlockNoteAndDate: true,
          bookingCta: {
            default: {
              buttonTitle: listingPanelTrans.hotelCTA.hotelButtonText(),
              buttonProps: {
                variant: 'solid',
                intent: 'newPrimary',
                _hover: { backgroundColor: { _: 'newUIBrandPrimary', md: 'buttonNewPrimaryHover' } },
              },
            },
          },
        },
      },
    };

    return config || defaultConfig;
  }, [config, listingPanelTrans.hotelCTA, sortTrans]);

  return <Provider value={contextValue}>{children}</Provider>;
};

export { AccommodationsConsoleConfigProvider, useAccommodationsConsoleConfig };
